<!-- FloatingLegalButton.vue -->
<script setup>
    import { ref, onMounted, onUnmounted, computed } from 'vue'
    import { router, usePage } from '@inertiajs/vue3'

    const page = usePage()
    const hideFloatingLegalButton = page.props?.hideFloatingLegalButton

    const isMenuOpen = ref(false)
    const buttonRef = ref(null)
    const menuRef = ref(null)
    const isVisible = ref(true)

    // Check if current page is Gabber/Lander
    const isGabberLanderPage = computed(() => {
        return page.props?.component === 'Gabber/Lander' || page.component === 'Gabber/Lander'
    })

    // Check if current page is Home/Landers
    const isHomeLandersPage = computed(() => {
        return page.props?.component === 'Home/Landers' || page.component === 'Home/Landers'
    })

    const menuItems = [
        { label: 'Terms of Service', href: route('tos') },
        { label: 'Privacy Policy', href: route('privacy') },
        { label: 'Content Guidelines', href: route('prohibitedContent') },
        { label: 'Support', href: route('support') },
        { label: '18 USC 2257', href: route('compliance') },
        { label: 'MasterCard Standards', href: route('complianceMastercard') },
        { label: 'Epoch.com', href: 'https://www.epoch.com/', external: true },
        { label: 'Segpay.com', href: 'https://cs.segpay.com/', external: true },
    ]

    const copyright = '©2025 Official Fans, Inc.'

    const closeMenu = () => {
        isMenuOpen.value = false
        buttonRef.value?.focus()
    }

    const toggleMenu = () => {
        isMenuOpen.value = !isMenuOpen.value
    }

    const handleClickOutside = event => {
        if (
            menuRef.value &&
            buttonRef.value &&
            !menuRef.value.contains(event.target) &&
            !buttonRef.value.contains(event.target)
        ) {
            closeMenu()
        }
    }

    const handleKeyDown = event => {
        // Only handle key events when the button is focused or menu is open
        if (!buttonRef.value?.contains(event.target) && !isMenuOpen.value) {
            return
        }

        if (!isMenuOpen.value) {
            if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
                event.preventDefault()
                isMenuOpen.value = true
            }
            return
        }

        switch (event.key) {
            case 'Escape':
                event.preventDefault()
                closeMenu()
                break
            case 'Tab':
                if (!menuRef.value?.contains(event.target)) {
                    closeMenu()
                }
                break
        }
    }

    const handleClick = item => {
        if (item.external) {
            // For external links, use a standard a tag behavior
            const a = document.createElement('a')
            a.href = item.href
            a.target = '_blank'
            a.rel = 'noopener noreferrer'
            a.click()
        } else {
            // For internal links, use Inertia navigation
            router.visit(item.href)
        }
        closeMenu()
    }

    const handleScroll = () => {
        // Always hide button on Gabber/Lander page (Chat page)
        if (isGabberLanderPage.value) {
            isVisible.value = false
            return
        }

        // Hide button on Home/Landers page
        if (isHomeLandersPage.value) {
            isVisible.value = true
            return
        }

        // Show button on all other pages
        isVisible.value = true
    }

    onMounted(() => {
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleKeyDown)
        window.addEventListener('scroll', handleScroll)

        // Initial check
        handleScroll()
    })

    onUnmounted(() => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', handleKeyDown)
        window.removeEventListener('scroll', handleScroll)
    })
</script>

<template>
    <div class="floating-legal-button" v-if="!hideFloatingLegalButton && isVisible">
        <!-- Floating Info Button -->
        <button
            ref="buttonRef"
            @click="toggleMenu"
            class="fixed bottom-[80px] right-5 z-[1000] flex h-12 w-12 items-center justify-center rounded-full border-2 border-mintgreen bg-white/10 text-xl font-medium text-mintgreen shadow-[0_4px_12px_rgba(0,0,0,0.3)] backdrop-blur-md transition-all duration-300 ease-out hover:scale-110 hover:shadow-[0_8px_24px_rgba(0,255,65,0.3)] focus:scale-110 focus:shadow-[0_8px_24px_rgba(0,255,65,0.3)] focus:outline-none focus:ring-2 focus:ring-mintgreen focus:ring-offset-2 focus:ring-offset-black active:scale-105"
            aria-label="Information and support menu"
            :aria-expanded="isMenuOpen"
            aria-haspopup="menu"
            aria-controls="info-menu"
            type="button">
            <span aria-hidden="true">ⓘ</span>
        </button>

        <!-- Dropdown Menu -->
        <div
            v-if="isMenuOpen"
            ref="menuRef"
            id="info-menu"
            role="menu"
            aria-labelledby="info-menu-button"
            class="fixed bottom-[118px] right-5 z-[1000] w-48 origin-bottom-right transform rounded-xl border border-[#333] bg-[rgba(20,20,20,0.95)] shadow-[0_8px_32px_rgba(0,0,0,0.5)] backdrop-blur-lg transition-all duration-200 ease-out">
            <div class="py-2" role="none">
                <a
                    v-for="(item, index) in menuItems"
                    :key="index"
                    :href="item.href"
                    role="menuitem"
                    tabindex="0"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="block border-b border-[#333]/50 px-4 py-2 text-sm text-white transition-colors duration-150 hover:bg-[#333] hover:text-mintgreen focus:outline-none focus:ring-2 focus:ring-inset focus:ring-mintgreen"
                    @click.prevent="handleClick(item)"
                    :aria-label="item.external ? `${item.label} (opens in new tab)` : item.label"
                    :aria-describedby="`menu-item-${index}-desc`">
                    {{ item.label }}
                    <span :id="`menu-item-${index}-desc`" class="sr-only">
                        Press Enter to open {{ item.label }} in a new tab
                    </span>
                </a>
                <!-- Copyright text -->
                <div
                    class="whitespace-nowrap border-t border-[#333]/50 px-4 py-3 text-sm text-gray-400">
                    {{ copyright }}
                </div>
            </div>
        </div>

        <!-- Screen reader announcement -->
        <div aria-live="polite" aria-atomic="true" class="sr-only">
            {{
                isMenuOpen
                    ? 'Information menu opened. Use arrow keys to navigate, Enter to select, Escape to close. Links will open in a new tab.'
                    : ''
            }}
        </div>
    </div>
</template>
